import { action, observable, makeObservable, runInAction } from "mobx";
import StockAPI from "../api/StockAPI";
import {
  DeleteSuggestedProductParams,
  Product,
  Stock,
  StockCheckSuggestedProducts,
} from "../../../models/Stock/Stock";
import { GetStockParams } from "../../../models/Stock/GetStockParams";
import { OrderProduct } from "../../../models/Order/OrderProduct";

export type StockCheckListTab = "all" | "open" | "closed";

export class StockStore {
  stockAPI: StockAPI;
  limit: number;
  total_pages: number;
  total_count: number;
  stocks: Stock[];
  orderStockCheckReqDetails: Stock[];
  stockCheckReq: Stock;
  eligibleProducts: OrderProduct[];
  useManualVendorEntry: boolean;
  vendorSearchInput: string;
  deleteConfirmSuggestedProductId: number | null;
  suggestedProductsModalOpen: boolean;
  suggestedProductsModalRequestId: number;
  suggestedProductsModalProduct: Product;
  suggestedProductsModalIsEditable: boolean;
  selectedSuggestedProducts: StockCheckSuggestedProducts[];
  listLoading: boolean;
  listError: string | null;
  filtersExpanded: boolean;
  private listRequestId: number;
  private lastListParams: GetStockParams | null;

  constructor() {
    this.stockAPI = new StockAPI();
    this.stocks = [];
    this.limit = 0;
    this.total_pages = 0;
    this.total_count = 0;
    this.orderStockCheckReqDetails = [];
    this.stockCheckReq = {};
    this.eligibleProducts = [];
    this.useManualVendorEntry = false;
    this.vendorSearchInput = "";
    this.deleteConfirmSuggestedProductId = null;
    this.suggestedProductsModalOpen = false;
    this.suggestedProductsModalRequestId = 0;
    this.suggestedProductsModalProduct = {};
    this.suggestedProductsModalIsEditable = false;
    this.selectedSuggestedProducts = [];
    this.listLoading = true;
    this.listError = null;
    this.filtersExpanded = true;
    this.listRequestId = 0;
    this.lastListParams = null;

    makeObservable(this, {
      stocks: observable,
      total_pages: observable,
      total_count: observable,
      listLoading: observable,
      listError: observable,
      filtersExpanded: observable,
      getStockList: action,
      loadStockList: action,
      refreshData: action,
      setListError: action,
      toggleFilters: action,
      getStockListByOrderId: action,
      orderStockCheckReqDetails: observable,
      raiseStockCheckRequest: action,
      updateStockCheckRequest: action,
      updateExecutionWarehouses: action,
      deleteSuggestedProduct: action,
      updateStatus: action,
      useManualVendorEntry: observable,
      vendorSearchInput: observable,
      deleteConfirmSuggestedProductId: observable,
      suggestedProductsModalOpen: observable,
      suggestedProductsModalRequestId: observable,
      suggestedProductsModalProduct: observable,
      suggestedProductsModalIsEditable: observable,
      selectedSuggestedProducts: observable,
      setUseManualVendorEntry: action,
      setVendorSearchInput: action,
      setDeleteConfirmSuggestedProductId: action,
      openSuggestedProductsModal: action,
      closeSuggestedProductsModal: action,
    });
  }

  setListError = (message: string | null) => {
    this.listError = message;
  };

  toggleFilters = () => {
    this.filtersExpanded = !this.filtersExpanded;
  };

  refreshData = async () => {
    if (!this.lastListParams) return;
    await this.loadStockList(this.lastListParams);
  };

  loadStockList = async (params: GetStockParams) => {
    this.lastListParams = params;
    const requestId = ++this.listRequestId;
    this.listLoading = true;
    this.listError = null;
    try {
      await this.getStockList(params);
    } catch (error: any) {
      if (requestId !== this.listRequestId) return;
      runInAction(() => {
        this.stocks = [];
        this.listError =
          error?.response?.data?.message ||
          "Something went wrong! Please try again later.";
      });
    } finally {
      if (requestId !== this.listRequestId) return;
      runInAction(() => {
        this.listLoading = false;
      });
    }
  };

  getStockList = async (params: GetStockParams) => {
    const requestId = this.listRequestId;
    try {
      this.stocks = [];
      const response = await this.stockAPI.getStockList(params);
      if (requestId !== this.listRequestId) return response;
      this.limit = response.limit;
      this.total_pages = response.total_pages;
      this.total_count = response.total_count;
      this.stocks = response.list;
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  getStockListByOrderId = async (orderId: number) => {
    try {
      const response = await this.stockAPI.getStockListByOrderId(orderId);
      this.orderStockCheckReqDetails = response;
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  getEligibleProducts = async (orderId: number): Promise<void> => {
    try {
      this.eligibleProducts = [];
      this.eligibleProducts = await this.stockAPI.getEligibleProductsByOrderId(
        orderId
      );
    } catch (error: any) {
      throw error;
    }
  };

  raiseStockCheckRequest = async (orderId: number, products: Product[]) => {
    try {
      await this.stockAPI.raiseStockCheckRequest(
        orderId,
        products
      );
    } catch (error: any) {
      throw error;
    }
  };

  updateExecutionWarehouses = async (
    orderId: number,
    requestId: number,
    warehouseIdentifiers: string[],
  ) => {
    try {
      const result = await this.stockAPI.updateExecutionWarehouses(
        orderId,
        requestId,
        warehouseIdentifiers,
      );
      runInAction(() => {
        const request = this.orderStockCheckReqDetails.find(
          (item) => item.id === requestId,
        );
        if (request) {
          request.warehouse_identifiers =
            result?.warehouse_identifiers ?? warehouseIdentifiers;
        }
      });
      return result;
    } catch (error: any) {
      throw error;
    }
  };

  updateStockCheckRequest = async (
    orderId: number,
    requestId: number,
    options: {
      status?: number;
      products?: Product[];
      suggestedProducts?: StockCheckSuggestedProducts[];
      warehouseIdentifiers?: string[];
    }
  ) => {
    try {
      const { status, products, suggestedProducts, warehouseIdentifiers } =
        options;
      let response;
      if (status !== undefined) {
        response = await this.stockAPI.updateStockCheckRequest(
          orderId,
          requestId!,
          {
            status,
            warehouseIdentifiers,
          }
        );
      } else {
        response = await this.stockAPI.updateStockCheckRequest(
          orderId,
          requestId!,
          {
            products,
            suggestedProducts,
          }
        );
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  deleteSuggestedProduct = async (
    params: DeleteSuggestedProductParams,
    orderId: number,
  ) => {
    try {
      const response = await this.stockAPI.deleteSuggestedProduct(
        params.requestId,
        params.productId,
        params.suggestedProductId,
      );

      const product = this.orderStockCheckReqDetails
        .find((req) => req.id === params.requestId)
        ?.products?.find((prod) => prod.product_id === params.productId);

      if (product?.suggested_products) {
        product.suggested_products = product.suggested_products.filter(
          (sp) => (sp.suggested_prod_id ?? sp.id) !== params.suggestedProductId,
        );
      }

      return response;
    } catch (error: any) {
      throw error;
    }
  };

  updateStatus = (requestId: number, status: number) => {
    this.orderStockCheckReqDetails.forEach((req) => {
      if (req.id === requestId) {
        req.status = status;
      }
    });
  };

  setUseManualVendorEntry = (value: boolean) => {
    this.useManualVendorEntry = value;
  };

  setVendorSearchInput = (value: string) => {
    this.vendorSearchInput = value;
  };

  setDeleteConfirmSuggestedProductId = (value: number | null) => {
    this.deleteConfirmSuggestedProductId = value;
  };

  openSuggestedProductsModal = (
    products: StockCheckSuggestedProducts[],
    requestId: number,
    product: Product,
    isEditable: boolean,
  ) => {
    this.selectedSuggestedProducts = products;
    this.suggestedProductsModalRequestId = requestId;
    this.suggestedProductsModalProduct = product;
    this.suggestedProductsModalIsEditable = isEditable;
    this.suggestedProductsModalOpen = true;
  };

  closeSuggestedProductsModal = () => {
    this.suggestedProductsModalOpen = false;
    this.selectedSuggestedProducts = [];
    this.suggestedProductsModalRequestId = 0;
    this.suggestedProductsModalProduct = {};
    this.suggestedProductsModalIsEditable = false;
    this.deleteConfirmSuggestedProductId = null;
  };
}
